import { CSSProperties, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import usePlans from 'Hooks/usePlans';
import { handleOrganizationPaymentUpdate_api } from 'api/paymentsV2Api';
import { goToUrl } from 'modules/Utils';
import { SHARED_TOAST_OPTIONS_ERROR } from 'modules/Toast';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import useUser from 'Hooks/useUser';
import { PlanType } from 'modules/Const';
import { Body2, Subtitle1 } from './Typography';

interface OrganizationPaymentModalProps {
  /** Whether to show the modal automatically when payment issues are detected */
  autoShow?: boolean;
  /** Manual control over modal visibility - overrides autoShow when provided */
  isOpen?: boolean;
  /** Callback when modal is closed */
  onClose?: () => void;
}

type PaymentScenario = 'trial_expired' | 'past_due' | 'paused' | null;

const OrganizationPaymentModal = ({
  autoShow = true,
  isOpen,
  onClose,
}: OrganizationPaymentModalProps) => {
  const { organization, getIsOrganizationPaymentPastDue, invalidateBillingStatus, showPaymentModal, closePaymentModal } = useOrganizationV2();
  const { getMonthlyPrice } = usePlans();
  const { isSymphonyAdmin } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hasShownThisSession, setHasShownThisSession] = useState(false);

  // Determine the current payment scenario
  const getPaymentScenario = (): PaymentScenario => {
    if (!organization) return null;

    // Check if trial has expired
    const isTrialExpired = organization.trial_end_date && new Date() > new Date(organization.trial_end_date);
    
    // Check subscription status
    const subscriptionStatus = organization.stripe_subscription_status;
    
    if (isTrialExpired && !organization.stripe_subscription_id) {
      return 'trial_expired';
    }
    
    if (subscriptionStatus === 'past_due') {
      return 'past_due';
    }
    
    if (subscriptionStatus === 'paused') {
      return 'paused';
    }
    
    return null;
  };

  // Get content based on the scenario
  const getModalContent = () => {
    const scenario = getPaymentScenario();
    const teamPrice = getMonthlyPrice(PlanType.TEAM_V2) || 199;
    
    switch (scenario) {
      case 'trial_expired':
        return {
          title: 'Your team plan trial has ended!',
          description: `Please add a payment method to continue using your team features at $${teamPrice}/month, or cancel your team to move your brands back to individual accounts.`,
          buttonText: 'Add Payment Method',
          showCancelTeam: true,
          icon: 'warning' as const,
          iconColor: Colors.red
        };
      
      case 'paused':
        return {
          title: 'Add a payment method to continue.',
          description: 'Your team trial has ended and your subscription is paused. Add a payment method to reactivate your team subscription and continue using all features.',
          buttonText: 'Add Payment Method',
          showCancelTeam: false,
          icon: 'warning' as const,
          iconColor: Colors.red
        };
      
      case 'past_due':
        return {
          title: 'Your team subscription is past due.',
          description: 'Your team subscription payment is past due. Please update your payment method to continue using Symphony\'s team features and avoid service interruption.',
          buttonText: 'Update Payment Method',
          showCancelTeam: false,
          icon: 'warning' as const,
          iconColor: Colors.red
        };
      
      default:
        return null;
    }
  };

  // Auto-show logic
  useEffect(() => {
    // Manual control takes precedence
    if (isOpen !== undefined) {
      setShowModal(isOpen);
      return;
    }

    // Check for manual trigger from store
    if (showPaymentModal) {
      setShowModal(true);
      return;
    }

    // Auto-show logic for payment issues
    const scenario = getPaymentScenario();
    const shouldShow = autoShow && 
                     scenario && 
                     !hasShownThisSession && 
                     organization && 
                     !isSymphonyAdmin;

    if (shouldShow) {
      setShowModal(true);
      setHasShownThisSession(true);
    }
  }, [
    isOpen, 
    showPaymentModal, 
    autoShow, 
    organization, // Single reference instead of individual fields
    isSymphonyAdmin
  ]);

  const handleManagePayment = async () => {
    if (!organization?.id) {
      toast.error('Unable to access billing details. Please try again.', SHARED_TOAST_OPTIONS_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      const result = await handleOrganizationPaymentUpdate_api({ organizationId: organization.id });
      
      // Show migration success message if migration was performed
      if (result.migrationPerformed) {
        toast.success('Your subscription has been updated to the latest pricing. Redirecting to billing portal...');
      }
      
      goToUrl(result.url, setIsLoading);
      
      // Invalidate billing status so it refreshes when user returns
      invalidateBillingStatus();
      
      handleCloseModal();
    } catch (error) {
      setIsLoading(false);
      Sentry.captureException(error);
      console.error('Error handling payment update:', error);
      toast.error('Something went wrong. Please try again or contact support.', SHARED_TOAST_OPTIONS_ERROR);
    }
  };

  const handleCancelTeam = async () => {
    if (!organization?.id) {
      toast.error('Unable to cancel team. Please try again.', SHARED_TOAST_OPTIONS_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      
      // TODO: Implement team cancellation API
      // This would:
      // 1. Set organization setup_status to 'disbanded'
      // 2. Move all brands back to FREE tier
      // 3. Remove user_brands access for non-owners
      // 4. Cancel Stripe subscription
      
      toast.success('Team cancelled successfully. Your brands have been moved to individual accounts.');
      handleCloseModal();
      
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      setIsLoading(false);
      Sentry.captureException(error);
      console.error('Error cancelling team:', error);
      toast.error('Something went wrong cancelling your team. Please contact support.', SHARED_TOAST_OPTIONS_ERROR);
    }
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    }
    
    // Close both local state and store state
    setShowModal(false);
    closePaymentModal();
  };

  const handleContactSupport = () => {
    const subject = `Team Plan Billing (id: ${organization?.id} / Name: ${organization?.name})`;
    const mailtoUrl = `mailto:megh@symphony.to?subject=${encodeURIComponent(subject)}`;
    window.open(mailtoUrl, '_blank');
  };

  const renderBrandsList = () => {
    const brands = organization?.brands || [];
    if (brands.length === 0) return null;

    return (
      <div style={sty.brandsList}>
        <Body2 style={sty.sectionTitle}>Brands in your team:</Body2>
        <div style={sty.brandsGrid}>
          {brands.slice(0, 6).map((brand, index) => (
            <div key={brand.id || index} style={sty.brandItem}>
              <div 
                style={{
                  ...sty.brandAvatar,
                  backgroundImage: brand.image ? `url(${brand.image})` : 'none'
                }}
              >
                {!brand.image && <Icon className="userIcon" icon="user" size={16} color={Colors.greyDark} />}
              </div>
              <Body2 style={sty.brandName}>{brand.name}</Body2>
            </div>
          ))}
          {brands.length > 6 && (
            <div style={sty.brandItem}>
              <div style={sty.brandAvatar}>
                <Body2 style={sty.moreCount}>+{brands.length - 6}</Body2>
              </div>
              <Body2 style={sty.brandName}>more</Body2>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!showModal || !organization) {
    return null;
  }

  const modalContent = getModalContent();
  if (!modalContent) {
    return null;
  }

  return (
    <ModalSymphony
      size="medium"
      className="organizationPaymentModal"
      isOpen={showModal}
      onClose={handleCloseModal}
      isHideCloseButton={!isSymphonyAdmin}
    >
      <div style={sty.mainContent}>
        <div style={sty.header}>
          <Icon 
            className="paymentModalIcon" 
            icon={modalContent.icon} 
            size={36} 
            color={modalContent.iconColor} 
          />
          <Subtitle1 style={sty.title}>{modalContent.title}</Subtitle1>
        </div>
        
        <div style={sty.body}>
          <Body2 style={sty.description}>
            {modalContent.description}
          </Body2>

          {getPaymentScenario() === 'trial_expired' && renderBrandsList()}
        </div>
        
        <div style={sty.actions}>
          <ButtonSymphony
            className="managePaymentButton"
            onClick={handleManagePayment}
            variant="primary"
            color="purple"
            isLoading={isLoading}
            disabled={isLoading}
            style={sty.primaryButton}
          >
            {isLoading ? 'Opening Billing Portal...' : modalContent.buttonText}
          </ButtonSymphony>

          {modalContent.showCancelTeam && (
            <ButtonSymphony
              className="cancelTeamButton"
              onClick={handleCancelTeam}
              variant="outlined"
              color="red"
              disabled={isLoading}
              style={sty.secondaryButton}
            >
              Cancel Team
            </ButtonSymphony>
          )}

          <ButtonSymphony
            className="contactSupportButton"
            onClick={handleContactSupport}
            variant="text"
            color="grey"
            style={sty.supportButton}
          >
            Contact Support
          </ButtonSymphony>
        </div>
      </div>
    </ModalSymphony>
  );
};

const sty: Record<string, CSSProperties> = {
  mainContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center'
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: Colors.red,
    margin: 0
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  description: {
    fontSize: 16,
    lineHeight: '1.5',
    color: Colors.greyDark,
    textAlign: 'center'
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: Colors.black,
    marginBottom: 8
  },
  brandsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  brandsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12
  },
  brandItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    backgroundColor: Colors.greyLight,
    borderRadius: 8
  },
  brandAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: Colors.greyLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },
  brandName: {
    fontSize: 14,
    color: Colors.black,
    fontWeight: 500
  },
  moreCount: {
    fontSize: 12,
    color: Colors.greyDark,
    fontWeight: 600
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'center'
  },
  primaryButton: {
    width: '100%',
    padding: '12px 24px',
    fontSize: 16,
    fontWeight: 600
  },
  secondaryButton: {
    width: '100%',
    fontSize: 14
  },
  supportButton: {
    fontSize: 14,
    color: Colors.greyDark,
    textDecoration: 'underline'
  }
};

export default OrganizationPaymentModal;
